// Configuration centralisée de l'environnement
// Ce fichier gère toutes les URLs et configurations de l'application

export interface EnvironmentConfig {
  apiBaseUrl: string;
  environment: 'development' | 'production' | 'staging';
  timeout: number;
  retryAttempts: number;
  enableLogging: boolean;
}

/**
 * Récupère l'URL du backend
 * 
 * ⚠️ IMPORTANT: Les variables RAILWAY_* (RAILWAY_PROJECT_NAME, RAILWAY_SERVICE_NAME, etc.)
 * sont celles du SERVICE FRONTEND, PAS du backend !
 * On NE PEUT PAS les utiliser pour construire l'URL du backend automatiquement.
 * 
 * VOUS DEVEZ définir REACT_APP_API_URL explicitement dans Railway !
 * 
 * 💰 CONSEIL RAILWAY: Utilisez RAILWAY_PRIVATE_DOMAIN pour éviter les frais d'egress
 * Exemple dans Railway:
 *   REACT_APP_API_URL=${{tanzeel-backend.RAILWAY_PRIVATE_DOMAIN}}
 * 
 * Priorité:
 * 1. REACT_APP_API_URL (OBLIGATOIRE pour Railway) ✅
 * 2. URL par défaut selon l'environnement (pour dev local uniquement)
 */
/**
 * Normalise une URL d'API : ajoute le protocole https:// si absent et
 * supprime les slashs finaux inutiles.
 *
 * Contexte : certains déploiements Railway définissent REACT_APP_API_URL
 * sans préfixe (ex: "elfalah-backend-production-xxx.up.railway.app").
 * Sans protocole, le navigateur interprète la valeur comme un chemin
 * RELATIF au frontend, et l'appel
 *   fetch("elfalah-backend.../api/public/landing-sections")
 * retourne le HTML du SPA au lieu de JSON, d'où
 *   SyntaxError: Unexpected token '<', "<!doctype "... is not valid JSON
 */
const normalizeApiUrl = (url: string): string => {
  if (!url) return url;
  const trimmed = url.trim().replace(/\/+$/, '');
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const getApiUrl = (localFallback: string): string => {
  // 1. Priorité : injection runtime via window._env_ (générée par server.js au démarrage)
  if (typeof window !== 'undefined' && (window as any)._env_?.REACT_APP_API_URL) {
    return normalizeApiUrl((window as any)._env_.REACT_APP_API_URL);
  }

  // 2. Fallback : variable build-time (fonctionne en dev local)
  if (process.env.REACT_APP_API_URL) {
    return normalizeApiUrl(process.env.REACT_APP_API_URL);
  }

  // 3. Dernier recours : fallback local
  if (!localFallback) {
    console.error('🚨 ERREUR CRITIQUE: REACT_APP_API_URL n\'est pas définie !');
    console.error('🚨 Définissez REACT_APP_API_URL dans les variables Railway et redéployez.');
  }
  return normalizeApiUrl(localFallback);
};

const getEnvNumber = (key: string, defaultValue: number): number => {
  const value = process.env[key];
  return value ? parseInt(value, 10) : defaultValue;
};

const getEnvBoolean = (key: string, defaultValue: boolean): boolean => {
  const value = process.env[key];
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === 'true';
};

// Configuration pour le développement local uniquement
const developmentConfig: EnvironmentConfig = {
  apiBaseUrl: getApiUrl('http://localhost:3001'),
  environment: 'development',
  timeout: getEnvNumber('REACT_APP_API_TIMEOUT', 10000),
  retryAttempts: getEnvNumber('REACT_APP_RETRY_ATTEMPTS', 3),
  enableLogging: getEnvBoolean('REACT_APP_ENABLE_LOGGING', true)
};

// ⚠️ REACT_APP_API_URL est OBLIGATOIRE pour staging et production
// Aucun fallback hardcodé — Railway DOIT définir cette variable
const stagingConfig: EnvironmentConfig = {
  apiBaseUrl: getApiUrl(''),
  environment: 'staging',
  timeout: getEnvNumber('REACT_APP_API_TIMEOUT', 15000),
  retryAttempts: getEnvNumber('REACT_APP_RETRY_ATTEMPTS', 3),
  enableLogging: getEnvBoolean('REACT_APP_ENABLE_LOGGING', true)
};

// ⚠️ REACT_APP_API_URL est OBLIGATOIRE pour staging et production
// Aucun fallback hardcodé — Railway DOIT définir cette variable
const productionConfig: EnvironmentConfig = {
  apiBaseUrl: getApiUrl(''),
  environment: 'production',
  timeout: getEnvNumber('REACT_APP_API_TIMEOUT', 15000),
  retryAttempts: getEnvNumber('REACT_APP_RETRY_ATTEMPTS', 3),
  enableLogging: getEnvBoolean('REACT_APP_ENABLE_LOGGING', false)
};

// Identique à production par défaut
const defaultConfig: EnvironmentConfig = productionConfig;

/**
 * Fonction pour obtenir la configuration selon l'environnement
 * Priorité:
 * 1. REACT_APP_ENVIRONMENT (si définie)
 * 2. NODE_ENV
 * 3. Configuration par défaut (production)
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  // Priorité à REACT_APP_ENVIRONMENT si définie
  const reactAppEnv = process.env.REACT_APP_ENVIRONMENT;
  if (reactAppEnv) {
    switch (reactAppEnv.toLowerCase()) {
      case 'development':
        return developmentConfig;
      case 'staging':
        return stagingConfig;
      case 'production':
        return productionConfig;
    }
  }

  // Sinon utiliser NODE_ENV
  const nodeEnv = process.env.NODE_ENV;
  switch (nodeEnv) {
    case 'development':
      return developmentConfig;
    case 'production':
      return productionConfig;
    default:
      return defaultConfig;
  }
};

// Configuration actuelle
export const ENV_CONFIG = getEnvironmentConfig();

// Afficher la configuration en mode développement (une seule fois)
if (ENV_CONFIG.enableLogging && typeof window !== 'undefined') {
  // Utiliser une variable globale pour éviter les logs multiples lors des re-renders
  if (!(window as any).__envConfigLogged) {
    (window as any).__envConfigLogged = true;
    
    const isPrivateDomain = ENV_CONFIG.apiBaseUrl.includes('.railway.internal');
    const isPublicDomain = ENV_CONFIG.apiBaseUrl.includes('.up.railway.app') || ENV_CONFIG.apiBaseUrl.includes('.railway.app');
    
    console.log('🔧 Configuration de l\'environnement:', {
      environment: ENV_CONFIG.environment,
      apiBaseUrl: ENV_CONFIG.apiBaseUrl,
      timeout: ENV_CONFIG.timeout,
      retryAttempts: ENV_CONFIG.retryAttempts,
      enableLogging: ENV_CONFIG.enableLogging,
      source: process.env.REACT_APP_API_URL ? 'REACT_APP_API_URL (définie)' : 'URL par défaut',
      networkType: isPrivateDomain ? '🔒 Réseau privé Railway (gratuit)' : 
                   isPublicDomain ? '🌐 Réseau public (avec frais egress)' : 
                   '💻 Développement local',
      railwayVars: {
        note: 'Ces variables sont celles du FRONTEND, pas du backend !',
        RAILWAY_ENVIRONMENT_NAME: process.env.RAILWAY_ENVIRONMENT_NAME,
        RAILWAY_PROJECT_NAME: process.env.RAILWAY_PROJECT_NAME,
        RAILWAY_SERVICE_NAME: process.env.RAILWAY_SERVICE_NAME,
      }
    });

    // Avertissement si REACT_APP_API_URL n'est pas définie en production/staging
    if (!process.env.REACT_APP_API_URL && ENV_CONFIG.environment !== 'development') {
      console.warn('⚠️ WARNING: REACT_APP_API_URL n\'est PAS définie !');
      console.warn('⚠️ L\'application utilise l\'URL par défaut, ce qui peut ne pas être correct.');
      console.warn('⚠️ Définissez REACT_APP_API_URL dans les variables d\'environnement Railway !');
    }
    
    // Info sur les frais d'egress si domaine public utilisé
    if (isPublicDomain) {
      console.info('💰 INFO: Vous utilisez le domaine public Railway (.up.railway.app)');
      console.info('💰 Cela engendre des frais d\'egress (sortie de données).');
      console.info('💰 Pour économiser, utilisez RAILWAY_PRIVATE_DOMAIN à la place :');
      // eslint-disable-next-line no-template-curly-in-string
      console.info('💰 REACT_APP_API_URL=${{tanzeel-backend.RAILWAY_PRIVATE_DOMAIN}}');
    }
    
    if (isPrivateDomain) {
      console.info('✅ EXCELLENT: Vous utilisez le réseau privé Railway (.railway.internal)');
      console.info('✅ Pas de frais d\'egress ! Communication interne gratuite.');
    }
  }
}

// Aucun fallback URL hardcodé — REACT_APP_API_URL est obligatoire en production/staging
export const FALLBACK_URLS: string[] = [];

// Headers par défaut
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
  'X-Requested-With': 'XMLHttpRequest'
};

// Fonction pour créer les headers avec authentification
export const createAuthHeaders = (token: string) => ({
  ...DEFAULT_HEADERS,
  'Authorization': `Bearer ${token}`
});

// Fonction pour créer les headers avec CORS
export const createCorsHeaders = () => ({
  ...DEFAULT_HEADERS,
  'Access-Control-Allow-Origin': '*',
  'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
  'Access-Control-Allow-Headers': 'Content-Type, Authorization'
});

export default ENV_CONFIG;
